import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { EnvelopeSimple, CheckCircle, WarningCircle, Globe, Phone, ArrowsClockwise } from "@phosphor-icons/react";

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);

  // Email config (admin only)
  const [emailConfig, setEmailConfig] = useState(null);
  const [domain, setDomain] = useState("");
  const [savingDomain, setSavingDomain] = useState(false);

  // 3CX sync (admin only)
  const [syncing3cx, setSyncing3cx] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/settings/email-config").then(({ data }) => {
        setEmailConfig(data);
        setDomain(data.sending_domain || "");
      }).catch(() => {});
    }
  }, [user?.role]);

  const updateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings/profile", { name });
      toast.success("Profile updated");
      checkAuth();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.put("/settings/password", { current_password: currentPw, new_password: newPw });
      toast.success("Password changed");
      setCurrentPw("");
      setNewPw("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveDomain = async () => {
    if (!domain.trim()) { toast.error("Enter a domain"); return; }
    setSavingDomain(true);
    try {
      const { data } = await api.put("/settings/email-config", { sending_domain: domain.trim() });
      toast.success(data.message);
      setEmailConfig(prev => ({ ...prev, sending_domain: domain.trim(), domain_verified: false }));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSavingDomain(false); }
  };

  const sync3cx = async () => {
    setSyncing3cx(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      const { data } = await api.post("/telephony/setup-3cx-production");
      setSyncResult(data);
      const conn = data.connection_test;
      if (conn?.connected) {
        toast.success(`3CX synced: ${data.mapped} mapped, ${data.pending} pending`);
      } else {
        toast.error(`3CX sync complete but connection failed: ${conn?.error || "unknown"}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Sync failed — check backend logs";
      const msg = typeof detail === "string" ? detail : JSON.stringify(detail);
      setSyncError(msg);
      toast.error(msg);
    } finally {
      setSyncing3cx(false);
    }
  };

  const roleLabel = { admin: "Administrator", opportunity_finder: "Opportunity Finder", candidate_finder: "Candidate Finder", team_leader: "Team Leader", senior_recruitment_partner: "Senior Recruitment Partner" }[user?.role] || user?.role;

  return (
    <div data-testid="settings-page" className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-[#0F172A]">Settings</h1>
        <p className="text-sm text-[#6B7280]">Manage your account settings</p>
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <h2 className="text-base font-semibold text-[#0F172A] mb-4">Account Information</h2>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-[#0F172A] flex items-center justify-center text-white text-xl font-bold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div>
            <p className="font-medium text-[#0F172A]">{user?.name}</p>
            <p className="text-sm text-[#6B7280]">{user?.email}</p>
            <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-[#0F172A] text-[#C5A059]">{roleLabel}</span>
          </div>
        </div>
      </div>

      {/* Edit Profile */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <h2 className="text-base font-semibold text-[#0F172A] mb-4">Edit Profile</h2>
        <form onSubmit={updateProfile} className="space-y-4">
          <div>
            <Label className="text-sm text-[#0F172A]">Display Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-10 border-[#E5E7EB] max-w-sm" data-testid="settings-name-input" />
          </div>
          <Button type="submit" disabled={saving} className="bg-[#0F172A] hover:bg-[#1E293B]" data-testid="save-profile-btn">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </form>
      </div>

      <Separator />

      {/* Change Password */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <h2 className="text-base font-semibold text-[#0F172A] mb-4">Change Password</h2>
        <form onSubmit={changePassword} className="space-y-4">
          <div>
            <Label className="text-sm text-[#0F172A]">Current Password</Label>
            <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required className="mt-1.5 h-10 border-[#E5E7EB] max-w-sm" data-testid="current-password-input" />
          </div>
          <div>
            <Label className="text-sm text-[#0F172A]">New Password</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required className="mt-1.5 h-10 border-[#E5E7EB] max-w-sm" data-testid="new-password-input" />
          </div>
          <Button type="submit" disabled={saving} variant="outline" className="border-[#E5E7EB]" data-testid="change-password-btn">
            {saving ? "Changing..." : "Change Password"}
          </Button>
        </form>
      </div>

      {/* Email Sending Domain (Admin Only) */}
      {user?.role === "admin" && emailConfig && (
        <>
          <Separator />
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-6" data-testid="email-config-section">
            <h2 className="text-base font-semibold text-[#0F172A] mb-1 flex items-center gap-2">
              <EnvelopeSimple size={18} className="text-[#C5A059]" /> Email Sending Domain
            </h2>
            <p className="text-xs text-[#6B7280] mb-4">Configure a verified domain to send invitation and notification emails from your own address.</p>

            <div className="bg-[#F9FAFB] rounded-lg p-4 mb-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#6B7280]">Current sender:</span>
                <span className="font-medium text-[#0F172A]">{emailConfig.sender_email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#6B7280]">API key:</span>
                {emailConfig.has_api_key ? (
                  <span className="flex items-center gap-1 text-green-600"><CheckCircle size={14} weight="fill" /> Configured</span>
                ) : (
                  <span className="flex items-center gap-1 text-red-500"><WarningCircle size={14} /> Not set</span>
                )}
              </div>
              {emailConfig.sending_domain && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#6B7280]">Domain:</span>
                  <span className="font-medium text-[#0F172A]">{emailConfig.sending_domain}</span>
                  {emailConfig.domain_verified ? (
                    <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Verified</span>
                  ) : (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Pending Verification</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-sm">
                <Label className="text-sm text-[#0F172A]">Sending Domain</Label>
                <div className="relative mt-1.5">
                  <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                  <Input
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="e.g. banksmorgan.com"
                    className="h-10 pl-9"
                    data-testid="email-domain-input"
                  />
                </div>
              </div>
              <Button
                onClick={saveDomain}
                disabled={savingDomain}
                className="bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] h-10"
                data-testid="save-domain-btn"
              >
                {savingDomain ? "Saving..." : "Save Domain"}
              </Button>
            </div>

            <div className="mt-4 bg-blue-50 rounded-lg p-3 text-xs text-blue-800 space-y-1">
              <p className="font-semibold">To verify your domain in Resend:</p>
              <ol className="list-decimal pl-4 space-y-0.5">
                <li>Go to <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline font-medium">resend.com/domains</a></li>
                <li>Add your domain (e.g. banksmorgan.com)</li>
                <li>Add the DNS records Resend provides to your domain registrar</li>
                <li>Click Verify in Resend once DNS has propagated</li>
              </ol>
            </div>
          </div>
        </>
      )}

      {/* 3CX Telephony Sync (Admin Only) */}
      {user?.role === "admin" && (
        <>
          <Separator />
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-6" data-testid="threecx-sync-section">
            <h2 className="text-base font-semibold text-[#0F172A] mb-1 flex items-center gap-2">
              <Phone size={18} className="text-[#C5A059]" /> 3CX Telephony
            </h2>
            <p className="text-xs text-[#6B7280] mb-4">
              Sync 3CX API configuration and map all agent extensions. Safe to run multiple times.
            </p>

            <Button
              onClick={sync3cx}
              disabled={syncing3cx}
              className="bg-[#0F172A] hover:bg-[#1E293B] text-white gap-2"
              data-testid="sync-3cx-btn"
            >
              <ArrowsClockwise size={16} className={syncing3cx ? "animate-spin" : ""} />
              {syncing3cx ? "Syncing..." : "Sync 3CX Extensions"}
            </Button>

            {syncError && !syncResult && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800" data-testid="sync-3cx-error">
                <p className="font-semibold mb-1">Sync Error</p>
                <p className="font-mono text-xs break-all">{syncError}</p>
              </div>
            )}

            {syncResult && (
              <div className="mt-4 bg-[#F9FAFB] rounded-lg p-4 space-y-3" data-testid="sync-3cx-result">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-[#6B7280]">Config:</span>
                  <span className="font-medium text-[#0F172A]">{syncResult.config}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1.5 text-green-600">
                    <CheckCircle size={14} weight="fill" /> {syncResult.mapped} mapped
                  </span>
                  {syncResult.pending > 0 && (
                    <span className="flex items-center gap-1.5 text-amber-600">
                      <WarningCircle size={14} /> {syncResult.pending} pending
                    </span>
                  )}
                </div>
                {syncResult.connection_test && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-[#6B7280]">Connection:</span>
                      <span className={`font-medium ${syncResult.connection_test.connected ? "text-green-600" : "text-red-500"}`}>
                        {syncResult.connection_test.connected ? "Connected" : "Failed"}
                      </span>
                      {syncResult.connection_test.provider === "mock" && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Mock Mode</span>
                      )}
                    </div>
                    {syncResult.connection_test.url && (
                      <div className="text-xs text-[#6B7280]">URL: {syncResult.connection_test.url}</div>
                    )}
                    {syncResult.connection_test.client_id && !syncResult.connection_test.connected && (
                      <div className="text-xs text-[#6B7280]">Client ID: {syncResult.connection_test.client_id}</div>
                    )}
                    {!syncResult.connection_test.connected && syncResult.connection_test.error && (
                      <div className="mt-1 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 font-mono break-all" data-testid="sync-3cx-connection-error">
                        {syncResult.connection_test.error}
                      </div>
                    )}
                  </div>
                )}
                {syncResult.env_diagnostic && (
                  <details className="text-xs text-[#6B7280]">
                    <summary className="cursor-pointer font-medium text-[#0F172A] mb-1">Credential diagnostic</summary>
                    <div className="bg-[#F3F4F6] rounded p-2 font-mono space-y-0.5">
                      {Object.entries(syncResult.env_diagnostic).map(([k, v]) => (
                        <div key={k}>{k}: {v}</div>
                      ))}
                    </div>
                  </details>
                )}
                {syncResult.details?.length > 0 && (
                  <details className="text-xs text-[#6B7280]">
                    <summary className="cursor-pointer font-medium text-[#0F172A] mb-1">Extension mapping details</summary>
                    <ul className="space-y-0.5 pl-2 max-h-48 overflow-y-auto">
                      {syncResult.details.map((d, i) => (
                        <li key={i} className={d.includes("PENDING") ? "text-amber-600" : ""}>{d}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
